import { useState } from 'react';
import {
  App as AntdApp,
  Button,
  Checkbox,
  DatePicker,
  Form,
  Input,
  Modal,
  Popconfirm,
  Select,
  Space,
  Table,
  Tag,
  Tooltip,
  Typography,
  Upload,
} from 'antd';
import { DeleteOutlined, EditOutlined, PlusOutlined, UploadOutlined } from '@ant-design/icons';
import { useMutation } from '@tanstack/react-query';
import { dobColumn, expiryColumn, genderColumn, issuerColumn, nationalityColumn } from './passengerColumns';
import dayjs, { type Dayjs } from 'dayjs';

import { DocThumbs, UpperInput, notFuture, notPast } from './passengerBits';
import { CountryAutocomplete } from './CountryAutocomplete';
import {
  addCharterPassenger,
  removeCharterPassenger,
  updateCharterPassenger,
  uploadFile,
} from '../api/endpoints';
import type { CharterPassenger, CharterPassengerInput } from '../api/types';
import { useSingleSubmit } from '../hooks/useSingleSubmit';
import { LIMITS } from '../data/limits';
import { CopyPassengerButton } from './CopyPassengerButton';

const { Text } = Typography;

/**
 * SHAR-115/122 — the charter passenger manifest with full CRUD.
 *
 * One component serves both places the manifest appears: under a booked quote
 * on the charter request page, and on the charter booking page. The two ACs ask
 * for the same thing, and an admin who learns the control in one place should
 * not meet a different one in the other.
 *
 * `onChanged` receives the refreshed payload from the API. Every mutation
 * returns the whole booking (or request) rather than just the passenger, so the
 * caller re-seeds its cache from the server instead of patching a local copy
 * and hoping it matches.
 */
export function CharterPassengers({
  bookingId,
  passengers,
  onChanged,
  readOnly = false,
  seatingCapacity,
}: {
  bookingId: string;
  passengers: CharterPassenger[];
  onChanged: (fresh: unknown) => void;
  readOnly?: boolean;
  /**
   * SHAR-687 — how many seats the quoted aircraft has.
   *
   * An admin could add passengers past that number: the manifest kept accepting
   * them and a flight cannot. The booking side has refused this since SHAR-440,
   * where the button goes disabled once a user's passengers reach their booked
   * seats, and this ticket asks for the same control rather than a second idea
   * of it.
   *
   * Undefined means the caller does not know the capacity, and then nothing is
   * enforced: a limit invented from a missing number is worse than none.
   */
  seatingCapacity?: number | null;
}) {
  const { message } = AntdApp.useApp();
  const [form] = Form.useForm();
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState<CharterPassenger | null>(null);
  const [passportUrl, setPassportUrl] = useState<string | null>(null);
  const [extraUrls, setExtraUrls] = useState<string[]>([]);
  const [uploading, setUploading] = useState(false);
  // SHAR-609 pattern — documents are staged for deletion and applied on Save,
  // so an admin who clicks the wrong bin on a passport can still back out.
  const [staged, setStaged] = useState<string[]>([]);
  const stage = (url: string) =>
    setStaged((prev) => (prev.includes(url) ? prev.filter((u) => u !== url) : [...prev, url]));

  // SHAR-687 — a manifest cannot outgrow the aeroplane.
  const atCapacity =
    seatingCapacity != null && seatingCapacity > 0 && passengers.length >= seatingCapacity;

  const fail = (fallback: string) => (e: any) =>
    message.error(e?.response?.data?.message || fallback);

  const addMut = useMutation({
    mutationFn: (body: CharterPassengerInput) => addCharterPassenger(bookingId, body),
    onSuccess: (fresh) => {
      message.success('Passenger added');
      setOpen(false);
      onChanged(fresh);
    },
    onError: fail('Add failed'),
  });
  const updateMut = useMutation({
    mutationFn: ({ pid, body }: { pid: string; body: Partial<CharterPassengerInput> }) =>
      updateCharterPassenger(pid, body),
    onSuccess: (fresh) => {
      message.success('Passenger updated');
      setOpen(false);
      onChanged(fresh);
    },
    onError: fail('Update failed'),
  });
  const removeMut = useMutation({
    mutationFn: (pid: string) => removeCharterPassenger(pid),
    onSuccess: (fresh) => {
      message.success('Passenger removed');
      onChanged(fresh);
    },
    onError: fail('Delete failed'),
  });

  const openAdd = () => {
    setEditing(null);
    setPassportUrl(null);
    setExtraUrls([]);
    setStaged([]);
    form.resetFields();
    setOpen(true);
  };
  const openEdit = (p: CharterPassenger) => {
    setEditing(p);
    setPassportUrl(p.passportPhotoUrl);
    setExtraUrls(p.extraDocUrls ?? []);
    setStaged([]);
    form.setFieldsValue({
      fullName: p.fullName,
      dateOfBirth: p.dateOfBirth ? dayjs(p.dateOfBirth) : null,
      gender: p.gender ?? undefined,
      nationality: p.nationality,
      passportPending: p.passportPending,
      passportNumber: p.passportNumber ?? '',
      issuingCountry: p.issuingCountry ?? '',
      passportExpiry: p.passportExpiry ? dayjs(p.passportExpiry) : null,
    });
    setOpen(true);
  };

  const doUpload = async (file: File, kind: 'passport' | 'extra') => {
    try {
      setUploading(true);
      const { url } = await uploadFile(file, 'passenger-doc');
      if (kind === 'passport') setPassportUrl(url);
      else setExtraUrls((u) => [...u, url]);
    } catch {
      message.error('Upload failed');
    } finally {
      setUploading(false);
    }
    return false;
  };

  // Regression 28 Sep — one submit at a time on this page's forms.
  const submitGuard = useSingleSubmit();
  const submit = submitGuard.wrap(async () => {
    const v = await form.validateFields();
    // SHAR-281 — "our team will reach out" defers the passport, so the passport
    // fields are cleared rather than saved half-filled.
    const defer = !!v.passportPending;
    const body: CharterPassengerInput = {
      fullName: v.fullName,
      dateOfBirth: v.dateOfBirth ? (v.dateOfBirth as Dayjs).format('YYYY-MM-DD') : '',
      gender: v.gender,
      nationality: v.nationality,
      passportPending: defer,
      passportNumber: defer ? undefined : v.passportNumber || undefined,
      issuingCountry: defer ? undefined : v.issuingCountry || undefined,
      passportExpiry:
        defer || !v.passportExpiry ? undefined : (v.passportExpiry as Dayjs).format('YYYY-MM-DD'),
      // SHAR-781 — DELETING THE PASSPORT PHOTO ACTUALLY DELETES IT.
      //
      // The Delete control under the thumbnail has always been there and always
      // worked in the modal: the thumbnail fades and offers Undo. It was the
      // save that dropped it. A staged photo was sent as `undefined`, and
      // `undefined` is the API's word for "leave this field alone" - so the
      // photo came straight back on the next read and the only thing an admin
      // could do was replace it with another one.
      //
      // Staged now sends `null`, which is the API's word for "clear it"
      // (charters.service.ts: `if (dto.passportPhotoUrl !== undefined)`).
      // `defer` keeps sending undefined: the passport block is not on screen at
      // all in that case, and the server clears those fields itself (SHAR-281).
      passportPhotoUrl: defer
        ? undefined
        : passportUrl && staged.includes(passportUrl)
          ? null
          : (passportUrl ?? undefined),
      extraDocUrls: extraUrls.filter((u) => !staged.includes(u)),
    };
    if (editing) await updateMut.mutateAsync({ pid: editing.id, body });
    else await addMut.mutateAsync(body);
  });

  const deferred = Form.useWatch('passportPending', form);

  return (
    <>
      <Space direction="vertical" size="small" style={{ width: '100%' }}>
        {readOnly ? null : (
          <div style={{ textAlign: 'right' }}>
            {/* SHAR-687 — disabled at capacity, with the reason on hover: the
                same control the booking manifest has had since SHAR-440. */}
            <Tooltip
              title={
                atCapacity
                  ? `This aircraft seats ${seatingCapacity}, and all ${seatingCapacity} seats have a passenger`
                  : ''
              }
            >
              <Button size="small" icon={<PlusOutlined />} onClick={openAdd} disabled={atCapacity}>
                Add passenger
              </Button>
            </Tooltip>
          </div>
        )}
        <Table<CharterPassenger>
          rowKey="id"
          dataSource={passengers}
          pagination={false}
          size="small"
          // Regression 29 Sep - a long unbroken value (an email, a passport
          // number) made this table wider than its card and the whole page
          // scrolled sideways; it now scrolls inside the card instead.
          scroll={{ x: 'max-content' }}
          locale={{ emptyText: 'No passengers' }}
          columns={[
            { title: 'Name', dataIndex: 'fullName', key: 'fullName' },
            dobColumn<CharterPassenger>(),
            genderColumn<CharterPassenger>(),
            nationalityColumn(),
            {
              title: 'Passport no.',
              key: 'pp',
              render: (_, p) =>
                p.passportPending ? <Tag>To be provided</Tag> : p.passportNumber || '—',
            },
            issuerColumn<CharterPassenger>(),
            expiryColumn<CharterPassenger>(),
            {
              title: 'Documents',
              key: 'docs',
              render: (_, p) => {
                const links: { url: string; label: string }[] = [];
                if (p.passportPhotoUrl) links.push({ url: p.passportPhotoUrl, label: 'Passport' });
                (p.extraDocUrls ?? []).forEach((u, i) =>
                  links.push({ url: u, label: `Doc ${i + 1}` }),
                );
                if (!links.length) return <Text type="secondary">—</Text>;
                return <DocThumbs docs={links} />;
              },
            },
            ...(readOnly
              ? [
                  {
                    // SHAR-127 — copying works on a read-only booking too.
                    title: '',
                    key: 'act',
                    width: 60,
                    render: (_: unknown, p: CharterPassenger) => <CopyPassengerButton passenger={p} />,
                  },
                ]
              : [
                  {
                    title: '',
                    key: 'act',
                    width: 130,
                    render: (_: unknown, p: CharterPassenger) => {
                      // SHAR-739 — the last passenger cannot be removed: a
                      // booking with an empty manifest is not a state this
                      // journey has, and the server refuses it too (the button
                      // is disabled so the admin is told before they try, not
                      // after). The customer's own remove has refused it since
                      // SHAR-716; this side had not.
                      const isLast = passengers.length <= 1;
                      const removeBtn = (
                        <Button size="small" danger icon={<DeleteOutlined />} disabled={isLast} />
                      );
                      return (
                        <Space>
                          {/* SHAR-127 — copy this passenger's details. */}
                          <CopyPassengerButton passenger={p} />
                          <Button size="small" icon={<EditOutlined />} onClick={() => openEdit(p)} />
                          {isLast ? (
                            <Tooltip title="A booking must keep at least one passenger. Add another before removing this one.">
                              {/* A disabled button swallows hover, so the tooltip
                                  hangs on a wrapper rather than on the button. */}
                              <span>{removeBtn}</span>
                            </Tooltip>
                          ) : (
                            /* The AC asks for deletion to happen via a pop-up. */
                            <Popconfirm
                              title="Remove this passenger?"
                              description="They are taken off the manifest for this booking."
                              okText="Remove"
                              okButtonProps={{ danger: true, loading: removeMut.isPending }}
                              onConfirm={() => removeMut.mutate(p.id)}
                            >
                              {removeBtn}
                            </Popconfirm>
                          )}
                        </Space>
                      );
                    },
                  },
                ]),
          ]}
        />
      </Space>

      <Modal
        title={editing ? 'Edit passenger' : 'Add passenger'}
        open={open}
        onCancel={() => setOpen(false)}
        onOk={submit}
        confirmLoading={submitGuard.busy || addMut.isPending || updateMut.isPending}
        okText={editing ? 'Save' : 'Add'}
        destroyOnClose
      >
        <Form form={form} layout="vertical">
          {/* SHAR-617 — identity fields are stored in capitals, as they read in
              a passport's machine-readable zone. */}
          <Form.Item name="fullName" label="Full name" rules={[{ required: true, message: 'Required' }]}>
            <UpperInput maxLength={LIMITS.name} />
          </Form.Item>
          <Form.Item name="dateOfBirth" label="Date of birth" rules={[{ required: true, message: 'Required' }]}>
            {/* SHAR-783 — a passenger cannot be born today or later. */}
            <DatePicker style={{ width: '100%' }} format="DD/MM/YYYY" disabledDate={notPast} />
          </Form.Item>
          <Form.Item name="gender" label="Gender" rules={[{ required: true, message: 'Required' }]}>
            <Select
              options={[
                { value: 'male', label: 'Male' },
                { value: 'female', label: 'Female' },
              ]}
            />
          </Form.Item>
          <Form.Item name="nationality" label="Nationality" rules={[{ required: true, message: 'Required' }]}>
            {/* SHAR-800 — filters as you type, matching the apps. */}
            <CountryAutocomplete placeholder="Start typing a country" />
          </Form.Item>

          <Form.Item name="passportPending" valuePropName="checked">
            <Checkbox>Our team will reach out for passport details</Checkbox>
          </Form.Item>

          {/* The passport block disappears when the details are deferred: fields
              that will not be saved should not be offered. */}
          {deferred ? null : (
            <>
              <Form.Item name="passportNumber" label="Passport number">
                <UpperInput maxLength={LIMITS.passportNumber} />
              </Form.Item>
              <Form.Item name="issuingCountry" label="Issuing country">
                <CountryAutocomplete placeholder="Start typing a country" />
              </Form.Item>
              <Form.Item name="passportExpiry" label="Passport expiry">
                {/* SHAR-785 — an expired passport is not valid for travel. */}
                <DatePicker style={{ width: '100%' }} format="DD/MM/YYYY" disabledDate={notFuture} />
              </Form.Item>
              <Form.Item label="Passport photo">
                <Space direction="vertical">
                  {passportUrl ? (
                    <DocThumbs
                      docs={[{ url: passportUrl, label: 'Passport' }]}
                      staged={staged}
                      onStage={stage}
                    />
                  ) : null}
                  <Upload beforeUpload={(f) => doUpload(f as File, 'passport')} showUploadList={false}>
                    <Button icon={<UploadOutlined />} loading={uploading}>
                      {passportUrl ? 'Replace' : 'Upload'}
                    </Button>
                  </Upload>
                </Space>
              </Form.Item>
            </>
          )}

          <Form.Item label="Other documents">
            <Space direction="vertical">
              {extraUrls.length ? (
                <DocThumbs
                  docs={extraUrls.map((u, i) => ({ url: u, label: `Doc ${i + 1}` }))}
                  staged={staged}
                  onStage={stage}
                />
              ) : null}
              <Upload beforeUpload={(f) => doUpload(f as File, 'extra')} showUploadList={false}>
                <Button icon={<UploadOutlined />} loading={uploading}>
                  Upload
                </Button>
              </Upload>
            </Space>
          </Form.Item>
        </Form>
      </Modal>
    </>
  );
}
